/**
 * Checkout / Undo checkout: API only, one dialog (pending → success/error), refresh documents on success.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ON_IFS_DOCUMENT_LOAD_CHANGED } from "../../constants/actionTypes";
import {
  CHECKOUTING_DOCUMENT,
  UNDO_CHECKOUT_PENDING,
  CHECKOUT_ERROR_MESSAGE,
  CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE,
  UNDO_CHECKOUT_ERROR_MESSAGE,
  ONEDRIVE_DOCUMENT_LOCKED_ERROR,
  UPLOADING_DOCUMENT_TO_ONEDRIVE,
} from "../../constants/messageTypes";
import {
  checkoutDocuments,
  checkoutDocumentsBulk,
  undoCheckoutDocumentsBulk,
  uploadToOneDrive,
  getWorkingDocuments,
  deleteOneDriveDoc,
  UPLOAD_TO_ONEDRIVE_TIMEOUT_MS,
} from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import { getDocClassCodeOnly } from "../../../functions/helperMethod";
import { serializeError } from "../../utils/serializeError";
import { removeWorkingDocumentByIdentifiers } from "../workingDocument/workingDocumentSlice";

const OPERATION_CHECKOUT = "Checkout";
const DOCUMENT_LOAD_ON_LOAD = "OnLoad";
const CHECKOUT_DIALOG_ERROR_CLOSE_MS = 2000;

/** Updates checkout dialog message from inside performCheckoutThunk. */
export const SET_CHECKOUT_FLOW_MESSAGE = "documents/documentCheckoutFlow/setMessage";

const userAuth = new UserAuth();

function resolveOneDriveUploadErrorMessage(uploadResult, uploadErr) {
  if (uploadResult?.status === 200) {
    return null;
  }
  if (uploadResult) {
    const data = uploadResult.data;
    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }
    if (data != null && typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
    return CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE;
  }
  const responseData = uploadErr?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }
  if (responseData != null && typeof responseData.message === "string") {
    return responseData.message;
  }
  if (typeof uploadErr?.message === "string" && uploadErr.message.trim()) {
    return uploadErr.message;
  }
  return CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE;
}

function isUndoCheckoutBlockedByOpenDocument(error) {
  const status = error?.response?.status ?? 0;
  if (status === 423) return true;

  const messageParts = [
    error?.response?.data,
    error?.response?.statusText,
    error?.message,
  ]
    .filter((value) => typeof value === "string" && value.trim().length > 0)
    .join(" ")
    .toLowerCase();

  return (
    messageParts.includes("in use") ||
    (messageParts.includes("open") && messageParts.includes("close"))
  );
}

function buildCheckoutPayload(doc, operationStatus) {
  return {
    webUser: new AuthTeams().getEmail(),
    docClass: getDocClassCodeOnly(doc.class ?? doc.docClass ?? ""),
    docNo: doc.docNo,
    docSheet: doc.sheetNo ?? doc.docSheet,
    docRev: doc.docRev ?? doc.docRev,
    docType: doc.docType,
    fileNo: `${doc.fileNo ?? 1}`,
    fileExtension: doc.fileExtension ?? "",
    title: doc.title ?? "",
    operationStatus,
    state: doc.state,
    fileState: doc.fileState,
    owner: doc.owner,
    lastActionDate:
      doc.lastActionDate ?? new Date().toISOString().split("T")[0],
    isDownloaded: doc.isDownloaded ?? false,
  };
}

/**
 * Build upload-to-onedrive items from checkout list (same shape as upload API expects).
 */
function buildUploadItems(checkoutList) {
  const safeDecode = (value) => {
    if (value == null) return "";
    const text = String(value);
    try {
      return decodeURIComponent(text);
    } catch {
      return text;
    }
  };

  return (checkoutList ?? []).map((doc) => ({
    class: getDocClassCodeOnly(doc.class ?? doc.docClass ?? ""),
    docNo: doc.docNo,
    sheetNo: doc.sheetNo ?? doc.docSheet,
    docRev: doc.docRev,
    docType: doc.docType,
    fileNo: doc.fileNo,
    // Checkout list stores encoded title; decode before OneDrive upload payload.
    title: safeDecode(doc.title),
    fileExtension: doc.fileExtension ?? "",
    state: doc.state,
    fileState: doc.fileState,
    owner: doc.owner,
    lastActionDate: doc.lastActionDate,
    isDownloaded: doc.isDownloaded ?? false,
  }));
}

function safeDecode(value) {
  if (value == null) return "";
  const text = String(value);
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function buildWorkingDocumentFileName(doc) {
  const title = safeDecode(doc?.title ?? "");
  const docClass = getDocClassCodeOnly(doc?.class ?? doc?.docClass ?? "");
  const docNo = doc?.docNo ?? "";
  const docSheet = doc?.sheetNo ?? doc?.docSheet ?? "";
  const docRev = doc?.docRev ?? "";
  const ext = doc?.fileExtension ?? "";
  return `${title} (${docClass} - ${docNo} - ${docSheet} - ${docRev}) - 1.${ext}`;
}

function getWorkingDocumentsListParams(uploadContext, searchText = "") {
  const oneDriveRoot =
    uploadContext.oneDriveRoot ??
    (uploadContext.channelId ? "ChannelDoc" : "MainMenu");

  return {
    teamId: uploadContext.teamId ?? "",
    channelId: uploadContext.channelId ?? "",
    tenantId: uploadContext.tenantId,
    fileRoot: oneDriveRoot,
    displayCount: 200,
    recFrom: 0,
    recSize: 200,
    fileType: "''",
    searchText,
  };
}

/** One OneDrive list fetch for undo cleanup (shared across all selected docs). */
async function listWorkingDocumentsOnce(accessToken, uploadContext) {
  if (!uploadContext?.tenantId) return [];
  const { data } = await getWorkingDocuments(
    accessToken,
    getWorkingDocumentsListParams(uploadContext, ""),
  );
  return Array.isArray(data) ? data : [];
}

function matchWorkingDocumentInList(workingDocs, doc) {
  if (!doc || !Array.isArray(workingDocs) || workingDocs.length === 0) {
    return null;
  }
  const expectedName = buildWorkingDocumentFileName(doc);
  return workingDocs.find((item) => item?.name === expectedName) ?? null;
}

function getWorkingDocumentFileId(workingDoc) {
  return workingDoc?.id ?? workingDoc?.documentId ?? workingDoc?.ItemsId ?? null;
}

/**
 * Delete OneDrive working copies for the given docs using a shared list cache.
 * Fetches get-working-documents at most once (reuses workingDocsCache when provided).
 */
async function deleteWorkingDocumentsForUndo({
  accessToken,
  uploadContext,
  docs,
  workingDocsCache = null,
  continueOnNonLockErrors = true,
}) {
  const list = docs ?? [];
  if (!uploadContext?.tenantId || list.length === 0) {
    return { deletedAny: false, workingDocsCache: workingDocsCache ?? [] };
  }

  const membershipType = uploadContext.membershipType ?? "Regular";
  const oneDriveRoot =
    uploadContext.oneDriveRoot ??
    (uploadContext.channelId ? "ChannelDoc" : "MainMenu");

  const workingDocs =
    workingDocsCache ?? (await listWorkingDocumentsOnce(accessToken, uploadContext));

  let deletedAny = false;
  await Promise.all(
    list.map(async (doc) => {
      const workingDoc = matchWorkingDocumentInList(workingDocs, doc);
      const fileId = getWorkingDocumentFileId(workingDoc);
      if (!fileId) return;

      try {
        await deleteOneDriveDoc(accessToken, {
          fileId,
          teamId: uploadContext.teamId ?? "",
          membershipType,
          oneDriveRoot,
          channelId: uploadContext.channelId ?? "",
          bypassSharedLock: false,
        });
        deletedAny = true;
      } catch (deleteErr) {
        if (isUndoCheckoutBlockedByOpenDocument(deleteErr)) {
          throw deleteErr;
        }
        if (!continueOnNonLockErrors) {
          throw deleteErr;
        }
        console.warn(
          "Pre-undo OneDrive delete failed; proceeding with undo checkout",
          deleteErr,
        );
      }
    }),
  );

  return { deletedAny, workingDocsCache: workingDocs };
}

function documentKey(doc) {
  const docClass = getDocClassCodeOnly(doc?.class ?? doc?.docClass ?? "");
  const docNo = doc?.docNo ?? "";
  const docSheet = doc?.sheetNo ?? doc?.docSheet ?? "";
  const docRev = doc?.docRev ?? "";
  return `${docClass}|${docNo}|${docSheet}|${docRev}`;
}

function resultDocumentKey(result) {
  const docClass = getDocClassCodeOnly(result?.docClass ?? "");
  const docNo = result?.docNo ?? "";
  const docSheet = result?.docSheet ?? "";
  const docRev = result?.docRev ?? "";
  return `${docClass}|${docNo}|${docSheet}|${docRev}`;
}

function getSuccessfulCheckoutDocs(checkoutList, bulkData) {
  const results = Array.isArray(bulkData?.results) ? bulkData.results : [];
  const successKeys = new Set(
    results
      .filter((item) => !item?.skipped && item?.statusCode === 200)
      .map((item) => resultDocumentKey(item)),
  );
  return (checkoutList ?? []).filter((doc) => successKeys.has(documentKey(doc)));
}

function resolveBulkCheckoutErrorMessage(err, fallback) {
  const responseData = err?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }
  if (responseData != null && typeof responseData.message === "string") {
    return responseData.message.trim();
  }
  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message.trim();
  }
  return fallback;
}

/**
 * First successful upload-to-onedrive item (for auto-open after single check-out).
 * Response shape: [{ statusCode, value: { webUrl, webDavUrl, name, ... } }, ...]
 */
function extractFileToOpenFromUploadResult(uploadResult) {
  if (uploadResult?.status !== 200) return null;
  const results = Array.isArray(uploadResult.data) ? uploadResult.data : [];
  if (results.length === 0) return null;

  const firstOk =
    results.find((item) => {
      const code = item?.statusCode ?? item?.StatusCode;
      return code === 200 || code === "OK" || code === "Ok";
    }) ?? results[0];

  const value = firstOk?.value ?? firstOk?.Value;
  if (!value || typeof value !== "object") return null;

  const webUrl = value.webUrl ?? value.WebUrl ?? null;
  const webDavUrl = value.webDavUrl ?? value.WebDavUrl ?? null;
  if (!webUrl && !webDavUrl) return null;

  return {
    id: value.id ?? value.Id ?? null,
    name: value.name ?? value.Name ?? null,
    webUrl,
    webDavUrl,
    sharepointIds: value.sharepointIds ?? value.SharepointIds ?? null,
    file: value.file ?? value.File ?? null,
  };
}

/** Payload: { checkoutList, operationStatus, uploadContext?, forceBulkCheckout?, forceBulkUndoCheckout?, suppressCheckoutUi? } */
export const performCheckoutThunk = createAsyncThunk(
  "documents/documentCheckoutFlow/performCheckout",
  async (
    {
      checkoutList,
      operationStatus,
      uploadContext,
      forceBulkCheckout,
      forceBulkUndoCheckout,
    },
    { dispatch, rejectWithValue },
  ) => {
    const isCheckout = operationStatus === OPERATION_CHECKOUT;
    const isBulkCheckout =
      isCheckout &&
      (forceBulkCheckout === true || (checkoutList?.length ?? 0) > 1);
    const isBulkUndo =
      !isCheckout &&
      (forceBulkUndoCheckout === true || (checkoutList?.length ?? 0) > 1);
    const errorMessage = isCheckout
      ? CHECKOUT_ERROR_MESSAGE
      : UNDO_CHECKOUT_ERROR_MESSAGE;

    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }

      let deletedBeforeUndo = false;
      let workingDocsCache = null;
      if (!isCheckout && uploadContext?.tenantId != null) {
        const preUndoResult = await deleteWorkingDocumentsForUndo({
          accessToken,
          uploadContext,
          docs: checkoutList,
          workingDocsCache: null,
          continueOnNonLockErrors: true,
        });
        deletedBeforeUndo = preUndoResult.deletedAny;
        workingDocsCache = preUndoResult.workingDocsCache;
      }

      const payload = checkoutList.map((doc) =>
        buildCheckoutPayload(doc, operationStatus),
      );

      let checkoutSucceeded = false;
      let bulkSummary = null;
      let docsForOneDriveUpload = checkoutList;
      let docsForUndoCleanup = checkoutList;

      if (isBulkCheckout) {
        const { status, data } = await checkoutDocumentsBulk(payload);
        const successCount = data?.successCount ?? 0;
        const failureCount = data?.failureCount ?? 0;
        const skippedCount = data?.skippedCount ?? 0;
        bulkSummary = {
          successCount,
          failureCount,
          skippedCount,
          results: data?.results ?? data?.Results ?? [],
        };

        if ((status === 200 || status === 201) && successCount > 0) {
          checkoutSucceeded = true;
          docsForOneDriveUpload = getSuccessfulCheckoutDocs(checkoutList, data);
        } else {
          setTimeout(() => {
            dispatch(closeCheckoutDialog());
          }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
          return rejectWithValue({
            message:
              (typeof data === "string" && data.trim()) ||
              data?.errorMessage ||
              errorMessage,
            bulk: bulkSummary,
          });
        }
      } else if (isBulkUndo) {
        const { status, data } = await undoCheckoutDocumentsBulk(payload);
        const successCount = data?.successCount ?? 0;
        const failureCount = data?.failureCount ?? 0;
        const skippedCount = data?.skippedCount ?? 0;
        bulkSummary = {
          successCount,
          failureCount,
          skippedCount,
          results: data?.results ?? data?.Results ?? [],
        };

        if ((status === 200 || status === 201) && successCount > 0) {
          checkoutSucceeded = true;
          docsForUndoCleanup = getSuccessfulCheckoutDocs(checkoutList, data);
        } else {
          setTimeout(() => {
            dispatch(closeCheckoutDialog());
          }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
          return rejectWithValue({
            message:
              (typeof data === "string" && data.trim()) ||
              data?.errorMessage ||
              errorMessage,
            bulk: bulkSummary,
          });
        }
      } else {
        const { status } = await checkoutDocuments(payload);
        checkoutSucceeded = status === 200;
      }

      if (checkoutSucceeded) {
        if (!isCheckout) {
          (docsForUndoCleanup ?? []).forEach((doc) => {
            dispatch(removeWorkingDocumentByIdentifiers(doc));
          });

          if (uploadContext?.tenantId != null && !deletedBeforeUndo) {
            await deleteWorkingDocumentsForUndo({
              accessToken,
              uploadContext,
              docs: docsForUndoCleanup,
              // Reuse the pre-undo list so we do not call get-working-documents again.
              workingDocsCache,
              continueOnNonLockErrors: false,
            });
          }
        }

        dispatch({
          type: ON_IFS_DOCUMENT_LOAD_CHANGED,
          payload: DOCUMENT_LOAD_ON_LOAD,
        });

        let fileToOpen = null;
        if (
          isCheckout &&
          uploadContext?.tenantId != null &&
          docsForOneDriveUpload.length > 0
        ) {
          dispatch({
            type: SET_CHECKOUT_FLOW_MESSAGE,
            payload: UPLOADING_DOCUMENT_TO_ONEDRIVE,
          });
          try {
            const uploadResult = await uploadToOneDrive(
              accessToken,
              {
                tenantId: uploadContext.tenantId,
                teamId: uploadContext.teamId ?? "",
                channelId: uploadContext.channelId ?? "",
                oneDriveRoot:
                  uploadContext.oneDriveRoot ??
                  (uploadContext.channelId ? "ChannelDoc" : "MainMenu"),
                items: buildUploadItems(docsForOneDriveUpload),
              },
              { timeout: UPLOAD_TO_ONEDRIVE_TIMEOUT_MS },
            );
            const failureMsg = resolveOneDriveUploadErrorMessage(
              uploadResult,
              null,
            );
            if (failureMsg) {
              setTimeout(() => {
                dispatch(closeCheckoutDialog());
              }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
              return rejectWithValue({
                message: failureMsg,
                checkoutSucceededIfs: true,
                bulk: bulkSummary,
              });
            }
            // Auto-open only for single-document check-out (same idea as template download).
            if (
              !isBulkCheckout &&
              docsForOneDriveUpload.length === 1
            ) {
              fileToOpen = extractFileToOpenFromUploadResult(uploadResult);
            }
          } catch (uploadErr) {
            const failureMsg = resolveOneDriveUploadErrorMessage(null, uploadErr);
            setTimeout(() => {
              dispatch(closeCheckoutDialog());
            }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
            return rejectWithValue({
              message: failureMsg,
              checkoutSucceededIfs: true,
              bulk: bulkSummary,
            });
          }
        }

        // Success text is shown only via toast (toastMiddleware). fulfilled reducer closes the dialog
        // without clearing lastOperationWasCheckout so All Documents can still navigate/refresh.
        return {
          variant: "info",
          bulk: bulkSummary,
          fileToOpen,
        };
      }
      setTimeout(() => {
        dispatch(closeCheckoutDialog());
      }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
      return rejectWithValue({ message: errorMessage });
    } catch (err) {
      const ser = serializeError(err);
      setTimeout(() => {
        dispatch(closeCheckoutDialog());
      }, CHECKOUT_DIALOG_ERROR_CLOSE_MS);
      if (!isCheckout && isUndoCheckoutBlockedByOpenDocument(err)) {
        return rejectWithValue({ message: ONEDRIVE_DOCUMENT_LOCKED_ERROR });
      }
      return rejectWithValue({
        message:
          isBulkCheckout || isBulkUndo
            ? resolveBulkCheckoutErrorMessage(err, errorMessage)
            : (ser?.message ?? errorMessage),
        bulk: null,
      });
    }
  },
);

const initialState = {
  loading: false,
  error: null,
  message: "",
  isDialogOpen: false,
  dialogVariant: "info",
  lastOperationWasCheckout: false,
  /** OneDrive file URLs to open after a successful single check-out (cleared after consume). */
  fileToOpen: null,
};

const documentCheckoutFlowSlice = createSlice({
  name: "documents/documentCheckoutFlow",
  initialState,
  reducers: {
    closeCheckoutDialog: (state) => {
      state.isDialogOpen = false;
      state.lastOperationWasCheckout = false;
    },
    clearCheckoutFileToOpen: (state) => {
      state.fileToOpen = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SET_CHECKOUT_FLOW_MESSAGE, (state, action) => {
        if (typeof action.payload === "string" && action.payload.trim()) {
          state.message = action.payload;
        }
      })
      .addCase(performCheckoutThunk.pending, (state, action) => {
        const isCheckout =
          action.meta?.arg?.operationStatus === OPERATION_CHECKOUT;
        const suppressUi = action.meta?.arg?.suppressCheckoutUi === true;
        const progressMessage = action.meta?.arg?.progressMessage;
        const checkoutCount = action.meta?.arg?.checkoutList?.length ?? 0;
        const isBulkCheckout =
          isCheckout &&
          (action.meta?.arg?.forceBulkCheckout === true || checkoutCount > 1);
        const isBulkUndo =
          !isCheckout &&
          (action.meta?.arg?.forceBulkUndoCheckout === true ||
            checkoutCount > 1);
        state.lastOperationWasCheckout = isCheckout;
        state.fileToOpen = null;
        if (suppressUi) {
          return;
        }
        state.loading = true;
        state.error = null;
        state.message =
          progressMessage ??
          (isBulkCheckout
            ? `Check-out in progress for ${checkoutCount} documents...`
            : isBulkUndo
              ? `Undo check-out in progress for ${checkoutCount} documents...`
              : isCheckout
                ? CHECKOUTING_DOCUMENT
                : UNDO_CHECKOUT_PENDING);
        state.isDialogOpen = true;
        state.dialogVariant = "info";
      })
      .addCase(performCheckoutThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = "";
        state.isDialogOpen = false;
        state.dialogVariant = action.payload?.variant ?? "info";
        state.fileToOpen = action.payload?.fileToOpen ?? null;
        // lastOperationWasCheckout left as set in pending (nav/refresh logic in DocumentsContent).
      })
      .addCase(performCheckoutThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeError(action.error);
        state.message = action.payload?.message ?? CHECKOUT_ERROR_MESSAGE;
        state.isDialogOpen = false;
        state.dialogVariant = "error";
        state.fileToOpen = null;
      });
  },
});

const { closeCheckoutDialog, clearCheckoutFileToOpen } =
  documentCheckoutFlowSlice.actions;

export { closeCheckoutDialog, clearCheckoutFileToOpen };
export default documentCheckoutFlowSlice.reducer;
